<script setup lang="ts">
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { computed } from 'vue'
import { formatDate } from '../utils/dateHelpers'

dayjs.extend(utc)

interface Meal {
  id: string
  title: string
  description: string
  recipe_id?: string
  scheduled_date?: string | null
  scheduled_dates?: string[]
  portions_days?: number
  portions_mode?: 'leftovers' | 'repeat'
  type_id?: string
}

interface MealType {
  id: string
  name: string
}

// Recipe interface is inferred from the GraphQL query result

const props = defineProps<{
  meals: Meal[]
  mealTypes: MealType[]
}>()

defineEmits<{
  (e: 'edit', meal: Meal): void
  (e: 'delete', meal: Meal): void
  (e: 'go-to-recipe', recipe_id: string): void
}>()

const findMealType = (id: string | undefined): string => {
  if (!props.mealTypes || !id) {
    return 'Desconocido'
  }

  const type = props.mealTypes.find((t) => t.id === id)
  return type ? type.name : 'Desconocido'
}

// Returns sorted [dateKey, meals] pairs; multi-day meals appear under each of their scheduled dates
const mealsByDate = computed((): [string, Meal[]][] => {
  const grouped: Record<string, Meal[]> = {}

  props.meals.forEach((meal) => {
    const dates = meal.scheduled_dates?.length ? meal.scheduled_dates : meal.scheduled_date ? [meal.scheduled_date] : []

    if (dates.length === 0) {
      ;(grouped['Unscheduled'] ??= []).push(meal)
      return
    }

    dates.forEach((d) => {
      const key = dayjs.utc(d).format('YYYY-MM-DD')
      ;(grouped[key] ??= []).push(meal)
    })
  })

  return Object.entries(grouped).sort(([a], [b]) => {
    if (a === 'Unscheduled') return 1
    if (b === 'Unscheduled') return -1
    return a < b ? -1 : a > b ? 1 : 0
  })
})
</script>

<template>
  <!-- Mobile Card View (hidden on desktop) -->
  <div class="block lg:hidden">
    <div v-for="[date, meals] in mealsByDate" :key="date">
      <div class="p-4 mt-4">
        <h2 class="text-md font-semibold text-gray-800">
          {{ date === 'Unscheduled' ? 'Unscheduled' : formatDate(date) }}
        </h2>
      </div>
      <div
        v-for="meal in meals"
        :key="`${date}-${meal.id}`"
        class="bg-white rounded-lg shadow-md border border-gray-200 p-4 hover:shadow-md transition-shadow mobile-card desktop-card-hover desktop-keyboard-nav"
        tabindex="0"
      >
        <div class="mb-3">
          <div class="mb-3">
            <h3 class="text-lg font-semibold text-gray-900 mb-0.5">{{ meal.title }}</h3>
            <p class="text-sm text-gray-600" v-if="meal.description">{{ meal.description }}</p>
          </div>

          <div class="grid grid-cols-2 gap-4 text-sm mb-4">
            <div>
              <span class="text-gray-500">Date:</span>
              <div class="font-medium text-gray-900">{{ formatDate(date) }}</div>
              <div
                v-if="(meal.portions_days ?? 1) > 1"
                class="mt-1 meal-portions-label"
                :class="
                  meal.portions_mode === 'repeat' ? 'meal-portions-label--repeat' : 'meal-portions-label--leftovers'
                "
              >
                <svg class="meal-portions-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    v-if="meal.portions_mode === 'repeat'"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                  />
                  <template v-else>
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1.001A3.75 3.75 0 0012 18z"
                    />
                  </template>
                </svg>
                <span>
                  {{ meal.portions_days }}d ·
                  {{ meal.portions_mode === 'repeat' ? 'Cook fresh each day' : 'Cooked Once' }}
                </span>
              </div>
            </div>
            <div>
              <span class="text-gray-500 inline-block mr-4">Type:</span>
              <span class="category-badge category-main-dishes inline-block">
                {{ findMealType(meal.type_id) }}
              </span>
            </div>
          </div>

          <div class="flex gap-2">
            <button
              v-if="meal.recipe_id"
              @click="$emit('go-to-recipe', meal.recipe_id)"
              class="btn-secondary flex items-center justify-center gap-2 min-h-[48px] text-sm font-medium flex-1 mobile-touch-target desktop-button-hover desktop-focus-ring"
              title="View recipe"
              tabindex="0"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <span>View</span>
            </button>
            <button
              @click="$emit('edit', meal)"
              class="btn-secondary flex items-center justify-center gap-2 min-h-[48px] text-sm font-medium flex-1 mobile-touch-target desktop-button-hover desktop-focus-ring"
              title="Edit meal"
              tabindex="0"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125"
                />
              </svg>
              <span>Edit</span>
            </button>
            <button
              @click="$emit('delete', meal)"
              class="flex items-center justify-center gap-2 px-4 py-3 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors min-h-[48px] text-sm font-medium flex-1 mobile-touch-target desktop-button-hover desktop-focus-ring"
              title="Delete meal"
              tabindex="0"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                />
              </svg>
              <span>Delete</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Desktop Table View (hidden on mobile) -->
  <div v-for="[date, meals] in mealsByDate" :key="date" class="hidden lg:block">
    <div class="bg-gray-50 p-4 mt-4">
      <h2 class="text-md font-semibold text-gray-800">
        {{ date === 'Unscheduled' ? 'Unscheduled' : formatDate(date) }}
      </h2>
    </div>
    <div class="overflow-x-auto">
      <table class="w-full table-fixed divide-y divide-gray-200">
        <colgroup>
          <col class="w-[38%]" />
          <col class="w-[18%]" />
          <col class="w-[14%]" />
          <col class="w-[30%]" />
        </colgroup>
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Meal</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr
            v-for="meal in meals"
            :key="`${date}-${meal.id}`"
            class="hover:bg-gray-50 transition-colors desktop-keyboard-nav"
            tabindex="0"
          >
            <!-- Meal: title + description stacked -->
            <td class="px-6 py-4 min-w-0">
              <p class="font-medium text-gray-900 truncate">{{ meal.title }}</p>
              <p v-if="meal.description" class="text-sm text-gray-500 truncate mt-0.5">{{ meal.description }}</p>
              <p v-else class="text-sm text-gray-400 italic mt-0.5">No description</p>
            </td>

            <!-- Date — shows group date + portions info as sub-line when multi-day -->
            <td class="px-6 py-4">
              <div class="text-sm text-gray-900 font-medium">{{ formatDate(date) }}</div>
              <div
                v-if="(meal.portions_days ?? 1) > 1"
                class="mt-1 meal-portions-label"
                :class="
                  meal.portions_mode === 'repeat' ? 'meal-portions-label--repeat' : 'meal-portions-label--leftovers'
                "
              >
                <svg class="meal-portions-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    v-if="meal.portions_mode === 'repeat'"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                  />
                  <template v-else>
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1.001A3.75 3.75 0 0012 18z"
                    />
                  </template>
                </svg>
                <!-- prettier-ignore -->
                <span>{{ meal.portions_days }}d · {{ meal.portions_mode === 'repeat' ? 'Cook fresh each day' : 'Cooked Once' }}</span>
              </div>
            </td>

            <!-- Type -->
            <td class="px-6 py-4 whitespace-nowrap">
              <span class="category-badge category-main-dishes">{{ findMealType(meal.type_id) }}</span>
            </td>

            <!-- Actions -->
            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
              <div class="flex justify-end gap-2">
                <button
                  v-if="meal.recipe_id"
                  @click="$emit('go-to-recipe', meal.recipe_id)"
                  class="btn-secondary flex items-center gap-1 min-h-[44px] text-sm font-medium mobile-touch-target desktop-button-hover desktop-focus-ring"
                  title="View recipe"
                  tabindex="0"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span class="hidden xl:inline">View</span>
                </button>
                <button
                  @click="$emit('edit', meal)"
                  class="btn-secondary flex items-center gap-1 min-h-[44px] text-sm font-medium mobile-touch-target desktop-button-hover desktop-focus-ring"
                  title="Edit meal"
                  tabindex="0"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125"
                    />
                  </svg>
                  <span class="hidden xl:inline">Edit</span>
                </button>
                <button
                  @click="$emit('delete', meal)"
                  class="flex items-center gap-1 px-3 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors min-h-[44px] text-sm font-medium mobile-touch-target desktop-button-hover desktop-focus-ring"
                  title="Delete meal"
                  tabindex="0"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                    />
                  </svg>
                  <span class="hidden xl:inline">Delete</span>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.meal-portions-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 4px;
  letter-spacing: 0.01em;
}

.meal-portions-label--leftovers {
  background: var(--color-primary-50, #edf3ec);
  color: var(--color-primary-700, #3f4a3c);
}

.meal-portions-label--repeat {
  background: #eff6ff;
  color: #1d4ed8;
}

.meal-portions-icon {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}
</style>
